import React, { useState, useEffect, useRef } from 'react'
import { Form, Button, Collapse, Select, Row, Col } from 'antd'
import { PlusOutlined, MinusOutlined } from '@ant-design/icons'
import MultiSelector from './MultiSelector'
import PeriodRangePicker from './PeriodRangePicker'
import { apiFetch } from "../utils/api";
import { useDateRange } from '../hooks/useDateRange'
import dayjs from 'dayjs'

const { Panel } = Collapse

export default function ReadingsFilter({
  devices,
  initialDevices = [],
  initialRange = null,
  onApplyFilters,
  disabledDevices = false,  
  elements,
  initialElements = [],
  disabledElements = false,
  defaultCollapsed = false,
  periodicity,
  setPeriodicity,
}) {
  const [selectedDevices, setSelectedDevices] = useState(initialDevices)
  const [selectedElements, setSelectedElements] = useState(initialElements)
  const [activeKey, setActiveKey] = useState(defaultCollapsed ? [] : ['filters'])
  const [loading, setLoading] = useState(false)
  const [deviceData, setDeviceData] = useState([]) // Nuevo estado para los datos de los dispositivos
  
  // Aquí definimos el estado filteredElements
  const [filteredElements, setFilteredElements] = useState([]) // Estado para almacenar los elementos filtrados

  const periodPickerRef = useRef()
  const [customRange, setCustomRange] = useState({
    from: dayjs().subtract(24, 'hour'),
    to: dayjs(),
  });
  const {
    showCustom,
    selectedLabel,
    setPreset,
    setShowCustom,
    selectedPreset,
    presetDefs,
  } = useDateRange(setCustomRange);

  useEffect(() => {
    setSelectedDevices(initialDevices)
  }, [initialDevices])

  useEffect(() => {
    setSelectedElements(initialElements)
  }, [initialElements])

  // Llamar al endpoint cada vez que selectedDevices cambie
  useEffect(() => {
    setFilteredElements([])
    if (selectedDevices.length === 0) return; // No hacer nada si no hay dispositivos seleccionados

    setLoading(true);  // Activamos el loading

    // Llamamos al API para obtener los elementos filtrados por dispositivos
    apiFetch('/api/elements/by-devices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: selectedDevices }), // Enviar los IDs seleccionados
      // credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setFilteredElements(data)) // Actualizamos los elementos filtrados
      .catch((err) => {
        console.error('Failed to load device data', err);
        setFilteredElements([]);  // En caso de error, vaciar los elementos
      })
      .finally(() => setLoading(false));  // Desactivamos el loading al finalizar
  }, [selectedDevices]); // Solo ejecutarse cuando selectedDevices cambie

  const handleApply = () => {
    if (!periodPickerRef.current) return;

    let range = periodPickerRef.current.getRange();

    if (!periodPickerRef.current.isCustom) {
      const preset = presetDefs.find(p => p.key === selectedPreset);
      if (preset) {
        const now = dayjs();
        range = {
          from: now.subtract(preset.n, preset.unit),
          to: now,
        };
      }
    }

    onApplyFilters(selectedDevices, selectedElements, range, periodicity);
  };

  const isOpen = Array.isArray(activeKey) ? activeKey.includes('filters') : activeKey === 'filters'
  const headerText = isOpen ? 'Hide filters' : 'Show filters'
  const headerIcon = isOpen
    ? <MinusOutlined style={{ fontSize: 13, lineHeight: 1, verticalAlign: 'middle' }} />
    : <PlusOutlined style={{ fontSize: 13, lineHeight: 1, verticalAlign: 'middle' }} />

  return (
    <Collapse
      activeKey={activeKey}
      onChange={setActiveKey}
      style={{ marginBottom: 10 }}
      expandIcon={() => null}
      items={[
        {
          key: 'filters',
          label: (
            <div style={{
              minHeight: 24,
              padding: '2px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 500,
              fontSize: 14
            }}>
              {headerIcon}
              <span>{headerText}</span>
            </div>
          ),
          children: (
            <Form layout="vertical">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <Form.Item label="Devices" style={{ minWidth: 250, marginBottom: '5px'}}>
                  <MultiSelector
                    items={devices}
                    value={selectedDevices}
                    onChange={setSelectedDevices}
                    placeholder="Select devices"
                    disabled={disabledDevices}
                  />
                </Form.Item>

                <Form.Item label="Elements" style={{ minWidth: 250, marginBottom: '5px' }}>
                  <MultiSelector
                    items={filteredElements}
                    value={selectedElements}
                    onChange={setSelectedElements}
                    placeholder="Select elements"
                    disabled={filteredElements.length === 0}
                    multiple
                  />
                </Form.Item>

                <Form.Item label="Aggregation Data" style={{ minWidth: 150, marginBottom: '5px' }}>
                  <Select
                    value={periodicity}
                    onChange={setPeriodicity}
                    placeholder="Select aggregation"
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="AVG5M">AVG 5M</Select.Option>
                    <Select.Option value="AVG15M">AVG 15M</Select.Option>
                    <Select.Option value="AVG1H">AVG 1H</Select.Option>
                    <Select.Option value="AVG24H">AVG 24H</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item label="Period" style={{ minWidth: 290, marginBottom: '5px' }}>
                  <PeriodRangePicker
                    ref={periodPickerRef}
                    value={customRange}
                    onChange={setCustomRange}
                    showCustom={showCustom}
                    setPreset={setPreset}
                    setShowCustom={setShowCustom}
                    selectedPreset={selectedPreset}
                    selectedLabel={selectedLabel}
                    presetDefs={presetDefs}
                  />
                </Form.Item>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'flex-end', marginBottom: '5px' }}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" onClick={handleApply} disabled={disabledDevices}>
                      Apply Filters
                    </Button>
                  </Form.Item>
                </div>
              </div>
            </Form>
          ),
          style: { padding: 0 }
        }
      ]}
    />

  )
}
